import { SummaryMetricType } from "../components/Reports/SummaryMetricsRow";
import {
  getReadableHoursAndMinutes,
  ONE_HOUR_OF_MS,
  ONE_MINUTE_OF_MS,
} from "./timeUtils";
import { useTargetsFromCustomerJson } from "./customerInfoUtils";
import { PnlBreakdown } from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { useTranslation } from "react-i18next";
import { round } from "lodash";
import { TFunction } from "i18next";
import { CustomerJsonThresholds } from "reports/types";
import { TimeUnitsEnum, MetricUnitsEnum, MetricEnum } from "./metricTypes";
import { useSelector } from "store/useSelector";
import { DataDumpAggregateType, DataDumpRow } from "types/types";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { GroupMetricType } from "components/DataGetter/FlightConfig/FlightConfigTypes/GroupMetricType";
import { useWorkScheduleIdOnCurrentPage } from "utils/customHooks/genericHooks";

const TIME_UNIT_TO_MAPPING = {
  [TimeUnitsEnum.milliseconds]: {
    divisor: 1,
    unitString: { short: "ms", long: "milliseconds" },
  },
  [TimeUnitsEnum.minutes]: {
    divisor: ONE_MINUTE_OF_MS,
    unitString: { short: "m", long: "min" },
  },
  [TimeUnitsEnum.hours]: {
    divisor: ONE_HOUR_OF_MS,
    unitString: { short: "h", long: "hrs" },
  },
};

type MetricUnitType = {
  unit?: keyof MetricUnitsEnum | keyof TimeUnitsEnum | string;
};

/*********************************************************************************************
 * METRICS
 ********************************************************************************************/
export const getOeePercent = ({
  oeePercent,
  oeePercentBottomUp,
  oeePercentTopDown,
}: PnlBreakdown) => {
  return oeePercent ?? oeePercentTopDown ?? oeePercentBottomUp;
};

export const getOeePercentTopDown = ({ oeePercentTopDown }: PnlBreakdown) =>
  oeePercentTopDown;
export const getOeePercentBottomUp = ({ oeePercentBottomUp }: PnlBreakdown) =>
  oeePercentBottomUp;

export const getAvailabilityPercent = ({ availabilityPercent }: PnlBreakdown) =>
  availabilityPercent;

export const getTeepPercent = ({
  teepPercent,
  teepPercentBottomUp,
}: PnlBreakdown) => teepPercent ?? teepPercentBottomUp;

export const getTeepPercentBottomUp = ({ teepPercentBottomUp }: PnlBreakdown) =>
  teepPercentBottomUp;

export const getUptimePercent = ({ uptimePercent }: PnlBreakdown) =>
  uptimePercent;

export const getTaggingPercent = ({ taggingPercent }: PnlBreakdown) =>
  taggingPercent;

export const getTaggingByEventPercent = ({
  taggingByEventPercent,
}: PnlBreakdown) => taggingByEventPercent;

export const getTaggedNumberOfEvents = ({
  taggedNumberOfEvents,
}: PnlBreakdown) => taggedNumberOfEvents;

export const getUntaggedNumberOfEvents = ({
  untaggedNumberOfEvents,
}: PnlBreakdown) => untaggedNumberOfEvents;

export const getProducingEfficiencyPercent = ({
  producingEfficiencyPercent,
}: PnlBreakdown) => producingEfficiencyPercent;

export const getPerformancePercent = ({
  performancePercent,
  performancePercentBottomUp,
}: PnlBreakdown) => performancePercent ?? performancePercentBottomUp;

export const getUslePercent = ({ uslePercent }: PnlBreakdown) => uslePercent;
export const getUntaggedPercent = ({ untaggedPercent }: PnlBreakdown) =>
  untaggedPercent;
export const getNotScheduledPercent = ({ notScheduledPercent }: PnlBreakdown) =>
  notScheduledPercent;
export const getCustomMetric01Value = ({ customMetric01Value }: PnlBreakdown) =>
  customMetric01Value;
export const getCustomMetric02Value = ({ customMetric02Value }: PnlBreakdown) =>
  customMetric02Value;
export const getCustomMetric03Value = ({ customMetric03Value }: PnlBreakdown) =>
  customMetric03Value;
export const getCustomMetric04Value = ({ customMetric04Value }: PnlBreakdown) =>
  customMetric04Value;
export const getCustomMetric05Value = ({ customMetric05Value }: PnlBreakdown) =>
  customMetric05Value;
export const getCustomMetric06Value = ({ customMetric06Value }: PnlBreakdown) =>
  customMetric06Value;
export const getCustomMetric07Value = ({ customMetric07Value }: PnlBreakdown) =>
  customMetric07Value;
export const getCustomMetric08Value = ({ customMetric08Value }: PnlBreakdown) =>
  customMetric08Value;

export const getOperationalAvailabilityPercent = ({
  operationalAvailabilityPercent,
}: PnlBreakdown) => operationalAvailabilityPercent;

export const getEfficiencyPercent = ({ efficiencyPercent }: PnlBreakdown) =>
  efficiencyPercent;

export const getQualityYieldPercent = ({ qualityYieldPercent }: PnlBreakdown) =>
  qualityYieldPercent;

export const getPureEquipmentUptimePercent = ({
  pureEquipmentUptimePercent,
}: PnlBreakdown) => pureEquipmentUptimePercent;

export const getOpportunityPercent = ({ opportunityPercent }: PnlBreakdown) =>
  opportunityPercent;

export const getMeanTimeBetweenFailures = ({
  meanTimeBetweenFailures,
}: PnlBreakdown) => meanTimeBetweenFailures;
export const getMeanTimeToRecovery = ({ meanTimeToRecovery }: PnlBreakdown) =>
  meanTimeToRecovery;
export const getAvgCycleTime = ({ avgCycleTime }: PnlBreakdown) =>
  // Convert from milliseconds to minutes.
  avgCycleTime ? avgCycleTime / 60 / 1000 : null;

export const getPlannedDowntimePercent = ({
  planned,
  productionTime,
}: PnlBreakdown) =>
  productionTime ? (planned.durationMs / productionTime) * 100 : null;

export const getUnplannedDowntimePercent = ({
  unplanned,
  productionTime,
}: PnlBreakdown) =>
  productionTime ? (unplanned.durationMs / productionTime) * 100 : null;

export const getWaitingDowntimePercent = ({
  waiting,
  productionTime,
}: PnlBreakdown) =>
  productionTime ? (waiting.durationMs / productionTime) * 100 : null;

export const getUnknownDowntimePercent = ({
  unknown,
  productionTime,
}: PnlBreakdown) =>
  productionTime ? (unknown.durationMs / productionTime) * 100 : null;

export const getProducingPercent = ({
  producing,
  productionTime,
}: PnlBreakdown) =>
  productionTime ? (producing.durationMs / productionTime) * 100 : null;

export const getSpeedLossPercent = ({
  producing,
  unknown,
  productionTime,
}: PnlBreakdown) =>
  productionTime
    ? (producing.slowdownGainLossTime + unknown.minorStops.durationMs) /
      productionTime
    : null;

/*********************************************************************************************
 * OUTPUTS
 ********************************************************************************************/
export const getCount = ({ netOutput }: PnlBreakdown) => netOutput;

export const getGrossCount = ({ grossOutput }: PnlBreakdown) => grossOutput;

export const getRejectCount = ({ rejectOutput }: PnlBreakdown) => rejectOutput;

export const getManualRejectCount = ({ manualRejectOutput }: PnlBreakdown) =>
  manualRejectOutput;

export const getTotalPotentialOutput = ({
  totalPotentialOutput,
}: PnlBreakdown) => totalPotentialOutput;

export const getAutoRejectCount = ({
  rejectOutput,
  manualRejectOutput,
}: PnlBreakdown) => rejectOutput - manualRejectOutput;

export const getProductionTargetOutput = ({
  productionTargetOutput,
}: PnlBreakdown) => productionTargetOutput;

export const getTicksPort1 = ({ ticksPort1 }: PnlBreakdown) => ticksPort1;
export const getTicksPort2 = ({ ticksPort2 }: PnlBreakdown) => ticksPort2;
export const getTicksPort3 = ({ ticksPort3 }: PnlBreakdown) => ticksPort3;
export const getTicksPort4 = ({ ticksPort4 }: PnlBreakdown) => ticksPort4;

/*********************************************************************************************
 * TIMES
 ********************************************************************************************/
export const getProducingTime = ({ producing }: PnlBreakdown) =>
  producing.durationMs;

export const getProductionTime = ({ productionTime }: PnlBreakdown) =>
  productionTime;

export const getPlannedTime = ({ planned }: PnlBreakdown) => planned.durationMs;

export const getUnplannedTime = ({ unplanned }: PnlBreakdown) =>
  unplanned.durationMs;

export const getWaitingTime = ({ waiting }: PnlBreakdown) => waiting.durationMs;

export const getNotScheduledTime = ({ notScheduled }: PnlBreakdown) =>
  notScheduled.durationMs;

export const getCollectedTime = ({ collectedTime }: PnlBreakdown) =>
  collectedTime;

export const getUncollectedTime = ({ uncollected }: PnlBreakdown) =>
  uncollected.durationMs;

export const getOpportunityTime = ({ opportunityTime }: PnlBreakdown) =>
  opportunityTime;

export const getOperationTime = ({ operationTime }: PnlBreakdown) =>
  operationTime;

/*********************************************************************************************
 * SKUS
 ********************************************************************************************/
export const getSku = ({ skuDescription }: PnlBreakdown) =>
  skuDescription ?? null;

export const getSkuCode = ({ sku }: PnlBreakdown) => sku ?? null;

export const getSkuTargetRate = ({
  skuTargetRate,
  skuRateDenomMs,
}: PnlBreakdown) =>
  skuTargetRate && skuRateDenomMs
    ? `${skuTargetRate} / ${
        skuRateDenomMs % ONE_HOUR_OF_MS === 0 ? "hour" : "min"
      }`
    : null;

export const getSkuTargetCount = ({
  skuTargetRate,
  skuRateDenomMs,
  productionTime,
}: PnlBreakdown) => {
  const production =
    productionTime && skuRateDenomMs
      ? productionTime /
        (skuRateDenomMs % ONE_HOUR_OF_MS == 0
          ? ONE_HOUR_OF_MS
          : ONE_MINUTE_OF_MS)
      : null;
  return skuTargetRate && production ? skuTargetRate * production : null;
};

export const getProcessName = ({ processNames }: PnlBreakdown) =>
  processNames?.[0] ?? null;

type MetricPropsType = {
  formula?: (input: PnlBreakdown) => number | null;
  stringFormula?: (input: PnlBreakdown) => string | null;
  unit?: string;
  shortName?: string;
  dataDumpKey?: string;
};
type MetricToPropsMappingType = {
  [metric in MetricEnum]: MetricPropsType;
};

export const MetricToPropsMapping: MetricToPropsMappingType = {
  [MetricEnum.OEE]: {
    formula: getOeePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "oeePercent",
  },
  [MetricEnum.OEE_TOP_DOWN]: {
    formula: getOeePercentTopDown,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "oeePercentTopDown",
  },
  [MetricEnum.OEE_BOTTOM_UP]: {
    formula: getOeePercentBottomUp,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "oeePercentBottomUp",
  },
  [MetricEnum.TEEP]: {
    formula: getTeepPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "teepPercent",
  },
  [MetricEnum.TEEP_BOTTOM_UP]: {
    formula: getTeepPercentBottomUp,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "teepPercentBottomUp",
  },
  [MetricEnum.UPTIME]: {
    formula: getUptimePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "uptimePercent",
  },
  [MetricEnum.AVAIL]: {
    formula: getAvailabilityPercent,
    unit: MetricUnitsEnum.percent,
    shortName: "Avail",
    dataDumpKey: "availabilityPercent",
  },
  [MetricEnum.TAGGING]: {
    formula: getTaggingPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "taggingPercent",
  },
  [MetricEnum.TAGGING_BY_EVENT]: {
    formula: getTaggingByEventPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "taggingByEventPercent",
  },
  [MetricEnum.TAGGED_COUNT]: {
    formula: getTaggedNumberOfEvents,
    unit: MetricUnitsEnum.count,
    dataDumpKey: "taggedNumberOfEvents",
  },
  [MetricEnum.UNTAGGED_COUNT]: {
    formula: getUntaggedNumberOfEvents,
    unit: MetricUnitsEnum.count,
    dataDumpKey: "untaggedNumberOfEvents",
  },
  [MetricEnum.PRODUCING_EFFICIENCY]: {
    formula: getProducingEfficiencyPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "producingEfficiencyPercent",
  },
  [MetricEnum.PERF]: {
    formula: getPerformancePercent,
    unit: MetricUnitsEnum.percent,
    shortName: "Perf.",
    dataDumpKey: "performancePercent",
  },
  [MetricEnum.USLE]: {
    formula: getUslePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "uslePercent",
  },
  [MetricEnum.OPERATIONAL_AVAILABILITY]: {
    formula: getOperationalAvailabilityPercent,
    unit: MetricUnitsEnum.percent,
    shortName: "OA",
    dataDumpKey: "operationalAvailabilityPercent",
  },
  [MetricEnum.EFFICIENCY]: {
    formula: getEfficiencyPercent,
    unit: MetricUnitsEnum.percent,
    shortName: "Eff.",
    dataDumpKey: "efficiencyPercent",
  },
  [MetricEnum.QUALITY]: {
    formula: getQualityYieldPercent,
    unit: "%",
    dataDumpKey: "qualityYieldPercent",
  },
  [MetricEnum.PURE_EQUIPMENT_UPTIME]: {
    formula: getPureEquipmentUptimePercent,
    unit: MetricUnitsEnum.percent,
    shortName: "Equip. Uptime",
    dataDumpKey: "pureEquipmentUptimePercent",
  },
  [MetricEnum.OPPORTUNITY]: {
    formula: getOpportunityPercent,
    unit: MetricUnitsEnum.percent,
    shortName: "Opp.",
    dataDumpKey: "opportunityPercent",
  },
  [MetricEnum.PLANNED_DOWNTIME]: {
    formula: getPlannedDowntimePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "plannedPercent",
  },
  [MetricEnum.PRODUCING_PERCENT]: {
    formula: getProducingPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "producingPercent",
  },

  [MetricEnum.PLANNED_DOWNTIME_PERCENT]: {
    formula: getPlannedDowntimePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "plannedPercent",
  },
  [MetricEnum.UNPLANNED_DOWNTIME_PERCENT]: {
    formula: getUnplannedDowntimePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "unplannedPercent",
  },
  [MetricEnum.WAITING_DOWNTIME_PERCENT]: {
    formula: getWaitingDowntimePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "waitingPercent",
  },
  [MetricEnum.UNKNOWN_DOWNTIME_PERCENT]: {
    formula: getUnknownDowntimePercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "unknownPercent",
  },
  [MetricEnum.COUNT]: { formula: getCount, unit: "", dataDumpKey: "netOutput" },
  [MetricEnum.NET_COUNT]: {
    formula: getCount,
    unit: "",
    dataDumpKey: "netOutput",
  },
  [MetricEnum.GROSS_COUNT]: {
    formula: getGrossCount,
    unit: "",
    dataDumpKey: "grossOutput",
  },
  [MetricEnum.REJECT_COUNT]: {
    formula: getRejectCount,
    unit: "",

    dataDumpKey: "rejectOutput",
  },
  [MetricEnum.MANUAL_REJECT_COUNT]: {
    formula: getManualRejectCount,
    unit: "",
    dataDumpKey: "manualRejectOutput",
  },
  [MetricEnum.AUTO_REJECT_COUNT]: {
    formula: getAutoRejectCount,
    unit: "",
    dataDumpKey: "autoRejectOutput",
  },
  [MetricEnum.PROD_POTENTIAL_OUTPUT]: {
    formula: getProductionTargetOutput,
    unit: "",
    dataDumpKey: "productionTargetOutput",
  },
  [MetricEnum.TOTAL_POTENTIAL_OUTPUT]: {
    formula: getTotalPotentialOutput,
    unit: "",
    dataDumpKey: "totalPotentialOutput",
  },

  [MetricEnum.TICKS_PORT1]: {
    formula: getTicksPort1,
    unit: "",
    dataDumpKey: "ticksPort1",
  },
  [MetricEnum.TICKS_PORT2]: {
    formula: getTicksPort2,
    unit: "",
    dataDumpKey: "ticksPort2",
  },
  [MetricEnum.TICKS_PORT3]: {
    formula: getTicksPort3,
    unit: "",
    dataDumpKey: "ticksPort3",
  },
  [MetricEnum.TICKS_PORT4]: {
    formula: getTicksPort4,
    unit: "",
    dataDumpKey: "ticksPort4",
  },

  [MetricEnum.PRODUCING_TIME]: {
    formula: getProducingTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Producing",
    dataDumpKey: "producingTime",
  },
  [MetricEnum.PRODUCTION_TIME]: {
    formula: getProductionTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Production",
    dataDumpKey: "productionTime",
  },
  [MetricEnum.PLANNED_TIME]: {
    formula: getPlannedTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Planned",
    dataDumpKey: "plannedTime",
  },
  [MetricEnum.UNPLANNED_TIME]: {
    formula: getUnplannedTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Unplanned",
    dataDumpKey: "unplannedTime",
  },
  [MetricEnum.WAITING_TIME]: {
    formula: getWaitingTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Waiting",
    dataDumpKey: "waitingTime",
  },
  [MetricEnum.NOT_SCHEDULED_TIME]: {
    formula: getNotScheduledTime,
    unit: TimeUnitsEnum.hours,
    shortName: "NPS",
    dataDumpKey: "notScheduledTime",
  },
  /* Duplicate of MetricEnum.OPERATION_TIME?
  [MetricEnum.COLLECTED_TIME]: {
    formula: getCollectedTime,
    unit: TimeUnitsEnum.hours,
  },
   */
  [MetricEnum.UNCOLLECTED_TIME]: {
    formula: getUncollectedTime,
    unit: TimeUnitsEnum.hours,
    dataDumpKey: "uncollectedTime",
  },
  [MetricEnum.OPPORTUNITY_TIME]: {
    formula: getOpportunityTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Opp. Time",
    dataDumpKey: "opportunityTime",
  },
  [MetricEnum.OPERATION_TIME]: {
    formula: getOperationTime,
    unit: TimeUnitsEnum.hours,
    shortName: "Operation",
    dataDumpKey: "operationTime",
  },
  [MetricEnum.SPEED_LOSS_PERCENT]: {
    formula: getSpeedLossPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "speedLossPercent",
  },

  [MetricEnum.SKU]: {
    stringFormula: getSku,
    unit: "",
  },
  [MetricEnum.SKU_CODE]: {
    stringFormula: getSkuCode,
    unit: "",
  },
  [MetricEnum.TARGET_RATE]: {
    stringFormula: getSkuTargetRate,
    unit: "",
  },
  [MetricEnum.TARGET_COUNT]: {
    formula: getSkuTargetCount,
    unit: "",
  },
  [MetricEnum.PROCESS_NAME]: {
    stringFormula: getProcessName,
    unit: "",
  },
  [MetricEnum.MEAN_TIME_BETWEEN_FAILURES]: {
    formula: getMeanTimeBetweenFailures,
    unit: TimeUnitsEnum.minutes,
    shortName: "MTBF",
    dataDumpKey: "meanTimeBetweenFailures",
  },
  [MetricEnum.MEAN_TIME_TO_RECOVERY]: {
    formula: getMeanTimeToRecovery,
    unit: TimeUnitsEnum.minutes,
    shortName: "MTTR",
    dataDumpKey: "meanTimeToRecovery",
  },
  [MetricEnum.AVG_CYCLE_TIME]: {
    formula: getAvgCycleTime,
    unit: TimeUnitsEnum.minutes,
    shortName: "Avg. Cycle",
    dataDumpKey: "avgCycleTime",
  },
  [MetricEnum.AVG_RUN_RATE]: {
    formula: getAverageRunRate,
    unit: "",
    shortName: "Avg. Rate",
  },
  [MetricEnum.AVG_RUN_RATE_PER_MINUTE]: {
    formula: getAverageRunRatePerMin,
    unit: "",
    shortName: "Avg. Rate (/min)",
  },
  [MetricEnum.UNTAGGED_PERCENT]: {
    formula: getUntaggedPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "untaggedPercent",
  },
  [MetricEnum.NOT_SCHEDULED_PERCENT]: {
    formula: getNotScheduledPercent,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "notScheduledPercent",
  },
  [MetricEnum.CUSTOM_METRIC_01]: {
    formula: getCustomMetric01Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric01Value",
  },
  [MetricEnum.CUSTOM_METRIC_02]: {
    formula: getCustomMetric02Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric02Value",
  },
  [MetricEnum.CUSTOM_METRIC_03]: {
    formula: getCustomMetric03Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric03Value",
  },
  [MetricEnum.CUSTOM_METRIC_04]: {
    formula: getCustomMetric04Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric04Value",
  },
  [MetricEnum.CUSTOM_METRIC_05]: {
    formula: getCustomMetric05Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric05Value",
  },
  [MetricEnum.CUSTOM_METRIC_06]: {
    formula: getCustomMetric06Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric06Value",
  },
  [MetricEnum.CUSTOM_METRIC_07]: {
    formula: getCustomMetric07Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric07Value",
  },
  [MetricEnum.CUSTOM_METRIC_08]: {
    formula: getCustomMetric08Value,
    unit: MetricUnitsEnum.percent,
    dataDumpKey: "customMetric08Value",
  },
  [MetricEnum.BLANK]: { formula: () => null, unit: "" },
};

export type SummaryMetricInput = MetricUnitType & {
  name?: MetricEnum;
  title?: string;
  shortName?: string;
  customValue?: number | null;
  decimalPoints?: number;
  order?: number;
  color?: string;
  description?: string;
  overrideMachemetryIds?: string[];
  dataDumpKey?: string;
};

/**
 * Get metric name and value from list of MetricEnum
 *
 * @export
 * @param {({
 *   t: TFunction;
 *   targets: any;
 *   metrics: SummaryMetricInput[];
 *   pnlBreakdown: PnlBreakdown | null;
 *   useShortName?: boolean;
 * })} {
 *   metrics,
 *   pnlBreakdown,
 *   useShortName = false,
 *   t,
 *   targets,
 * }
 * @return {*}
 */
export function getSummaryMetrics({
  metrics,
  pnlBreakdown,
  useShortName = false,
  t,
  targets,
  dataDumpsFullRangeOverrideMachemetryIds,
  flightConfigMetrics,
}: {
  metrics: SummaryMetricInput[];
  pnlBreakdown: PnlBreakdown | null;
  useShortName?: boolean;
  t: TFunction;
  targets?: CustomerJsonThresholds;
  dataDumpsFullRangeOverrideMachemetryIds?: {
    [processIds: string]: DataDumpAggregateType;
  };
  flightConfigMetrics?: GroupMetricType[];
}) {
  return !pnlBreakdown
    ? []
    : metrics.map(
        ({
          name: _name,
          shortName,
          title,
          color,
          unit,
          customValue,
          decimalPoints,
          order,
          description,
          overrideMachemetryIds,
          dataDumpKey,
        }) => {
          const nameFromDataDumpKey = dataDumpKey
            ? Object.entries(MetricToPropsMapping).find(
                ([_key, value]) => value.dataDumpKey === dataDumpKey
              )?.[0]
            : _name;
          const name = nameFromDataDumpKey ?? _name ?? null;
          const metricProps: MetricPropsType = MetricToPropsMapping[name ?? ""];
          const metricUnit = unit ?? metricProps?.unit;
          const metricDecimalPoints =
            (decimalPoints ?? metricUnit === MetricUnitsEnum.percent) ? 1 : 0;

          // Use overridden value or default value from formula
          // Use customValue if it's null (because that may be what the custom formula returned) but not if undefined
          let metricValue =
            customValue !== undefined
              ? customValue
              : metricProps?.formula
                ? metricProps.formula(pnlBreakdown)
                : null;

          // if this metric has overrideMachemetryIds configured, look for its value in the associated override dataDump
          if (
            dataDumpKey &&
            dataDumpsFullRangeOverrideMachemetryIds &&
            overrideMachemetryIds &&
            overrideMachemetryIds.length > 0
          ) {
            const dataDumpOverride = Object.entries(
              dataDumpsFullRangeOverrideMachemetryIds
            ).find(
              ([machemetryIds, dataDump]) =>
                machemetryIds === overrideMachemetryIds.join(",")
            )?.[1];
            if (dataDumpOverride) {
              metricValue = dataDumpOverride?.[dataDumpKey];
            }
          }

          const metricStringValue = metricProps?.stringFormula
            ? metricProps.stringFormula(pnlBreakdown)
            : null;

          // Check if unit is measuring time
          const isTimeMetric = Object.values(TimeUnitsEnum).includes(
            metricUnit as TimeUnitsEnum
          );

          // Override metric name if title provided, otherwise use default (shortname or longname)
          const metricName = title
            ? title
            : useShortName && metricProps?.shortName
              ? t(metricProps?.shortName)
              : useShortName
                ? t(metricProps?.shortName || name || "")
                : name
                  ? t(name)
                  : "";

          const showBlankValue = name === MetricEnum.BLANK;

          // Get props for metrics measured by time
          const { numericalTime, stringTime, timeUnitSuffix } =
            getRawNumberAndStringFormattedTime({
              timeInMs: metricValue ?? 0,
              unit: metricUnit as TimeUnitsEnum,
              decimalPoints: metricDecimalPoints,
            });

          const numericValue = showBlankValue
            ? null
            : isTimeMetric
              ? numericalTime
              : typeof metricValue === "number"
                ? round(metricValue, metricDecimalPoints)
                : metricValue;

          // Use stringFormattedValue when unit is within number
          // E.g. 3h 5min
          const stringFormattedValue =
            isTimeMetric && timeUnitSuffix === undefined
              ? stringTime
              : metricStringValue
                ? metricStringValue
                : undefined;

          const metricTarget = targets?.[name ?? ""] * 100;
          const metricColor = color
            ? color
            : metricTarget
              ? numericValue === 0 ||
                (numericValue && numericValue < metricTarget)
                ? "red"
                : "green"
              : undefined;

          const maxNumChars = getMaxCharsByUnit(
            metricUnit as MetricUnitType,
            isTimeMetric,
            metricDecimalPoints
          );

          return {
            name: name ?? "",
            nameTranslated: metricName,
            numericValue,
            stringFormattedValue,
            unit: isTimeMetric ? timeUnitSuffix : metricUnit,
            order,
            color: metricColor,
            maxNumChars,
            description:
              description ??
              flightConfigMetrics?.find(
                metric => metric.dataDumpKey === dataDumpKey
              )?.description,
          };
        }
      );
}

/**
 * Get max numbers of chars the value could be, based on the unit
 * Using a week as reference for times, since EOS reports are max, 1 week at this time
 * This value is only used in the machine/shift/process breakdown
 *
 * @param {MetricUnitType} unit
 * @param {boolean} isTimeMetric
 * @param {*} metricDecimalPoints
 * @return {*}  {number}
 */
function getMaxCharsByUnit(
  unit: MetricUnitType,
  isTimeMetric: boolean,
  metricDecimalPoints
): number {
  const defaultMaxNumUnits = 10;
  // If more than 0 decimal places, add one for actual decimal point
  let maxCharsPerUnit = metricDecimalPoints === 0 ? 0 : metricDecimalPoints + 1;

  if (isTimeMetric) {
    maxCharsPerUnit +=
      unit === String(TimeUnitsEnum.milliseconds)
        ? // e.g. 1637597548000
          12
        : unit === String(TimeUnitsEnum.minutes) ||
            unit === String(TimeUnitsEnum.hours)
          ? // e.g. 10080 m or 168 hrs
            7
          : defaultMaxNumUnits;
  } else {
    maxCharsPerUnit +=
      unit === String(MetricUnitsEnum.percent)
        ? 6
        : // e.g. 1000000000
          MetricUnitsEnum.count
          ? 10
          : defaultMaxNumUnits;
  }
  return maxCharsPerUnit;
}

/**
 * Hook to metric name and value from list of MetricEnum
 *
 * @param {{
 *   metrics: SummaryMetricInput[];
 *   pnlBreakdown: PnlBreakdown;
 *   useShortName?: boolean;
 * }} {
 *   metrics,
 *   pnlBreakdown,
 *   useShortName = false
 * }
 * @return {*}  {SummaryMetricType[]}
 */
export const useSummaryMetrics = ({
  metrics,
  pnlBreakdown,
  useShortName = false,
  targetsOverride,
}: {
  metrics: SummaryMetricInput[];
  pnlBreakdown: PnlBreakdown | null;
  useShortName?: boolean;
  targetsOverride?: CustomerJsonThresholds;
}): SummaryMetricType[] => {
  const { t } = useTranslation("EOSReport");
  const targets = useTargetsFromCustomerJson();
  const dataDumpsFullRangeOverrideMachemetryIds = useSelector(
    state => state.legacy.dataDumpsFullRangeOverrideMachemetryIds
  );
  const [{ metrics: flightConfigMetrics }] = useFlightConfig();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const flightConfigMetricsForWorkSchedule =
    flightConfigMetrics[workScheduleId];
  return getSummaryMetrics({
    metrics,
    pnlBreakdown,
    useShortName,
    t,
    targets: targetsOverride || targets,
    dataDumpsFullRangeOverrideMachemetryIds,
    flightConfigMetrics: flightConfigMetricsForWorkSchedule,
  });
};

/**
 * Get numerical time, formatted time string and unit suffix for time related metric
 *
 * @param {{
 *   timeInMs: number;
 *   unit: TimeUnitsEnum;
 *   decimalPoints?: number;
 * }} {
 *   timeInMs,
 *   unit,
 *   decimalPoints,
 * }
 * @return {*}
 */
const getRawNumberAndStringFormattedTime = ({
  timeInMs,
  unit,
  decimalPoints,
}: {
  timeInMs: number;
  unit: TimeUnitsEnum;
  decimalPoints?: number;
}) => {
  const timeUnitMapping = TIME_UNIT_TO_MAPPING?.[unit];

  const numericalTime = [
    TimeUnitsEnum.milliseconds,
    TimeUnitsEnum.minutes,
    TimeUnitsEnum.hours,
  ].includes(unit)
    ? round(
        timeInMs / timeUnitMapping.divisor,
        decimalPoints ? decimalPoints : 0
      )
    : null;

  const stringTime =
    timeUnitMapping &&
    [
      TimeUnitsEnum.milliseconds,
      TimeUnitsEnum.minutes,
      TimeUnitsEnum.hours,
    ].includes(unit)
      ? `${numericalTime}${timeUnitMapping.unitString.short}`
      : getReadableHoursAndMinutes(timeInMs);

  return {
    numericalTime,
    stringTime,
    timeUnitSuffix: timeUnitMapping?.unitString.long,
  };
};

export const sortByOrder = (a, b) =>
  (a.order || a.order === 0) && (b.order || b.order === 0)
    ? a.order - b.order
    : 0;

export const getMetricUnit = metricName => {
  return MetricToPropsMapping?.[metricName ?? ""]?.unit ?? "";
};

export function getAverageRate(data: PnlBreakdown) {
  const averageRate =
    data.grossOutput && data.productionTime
      ? data.grossOutput / (data.productionTime / ONE_HOUR_OF_MS)
      : 0;

  return averageRate;
}

export function getAverageRunRate(data: PnlBreakdown) {
  const averageRunRate =
    data.grossOutput && data.producing.durationMs
      ? data.grossOutput / (data.producing.durationMs / ONE_HOUR_OF_MS)
      : 0;

  return averageRunRate;
}
export function getAverageRunRatePerMin(data: PnlBreakdown) {
  const averageRunRate =
    data.grossOutput && data.producing.durationMs
      ? data.grossOutput / (data.producing.durationMs / ONE_MINUTE_OF_MS)
      : 0;

  return averageRunRate;
}

export function getAverageRunRateForDataDumpRow(data: DataDumpRow) {
  const isProducing = data.pnlCategory === "producing";

  if (!isProducing) {
    return null;
  }

  const rowDuration = data.ge && data.lt && Number(data.lt) - Number(data.ge);

  const averageRunRate =
    data?.grossOutput && rowDuration
      ? data.grossOutput / (rowDuration / ONE_HOUR_OF_MS)
      : 0;

  return averageRunRate;
}
